import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { useAuth, useClerk } from "@clerk/clerk-react";
import { shutdownIntercom } from "../../lib/intercom";
import { useThemeColors } from "../../hooks/useThemeColors";
import { useTheme } from "../../contexts/ThemeContext";

export default function Nav({ scrolled }: { scrolled: boolean }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const c = useThemeColors();
  useTheme(); // retained for potential future theme-aware logo

  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  let isSignedIn = false;
  let signOut: (() => void) | null = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const auth = useAuth();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const clerk = useClerk();
    isSignedIn = auth.isSignedIn ?? false;
    signOut = () => { shutdownIntercom(); void clerk.signOut(() => void navigate('/')); };
  } catch {}

  function isActive(href: string) {
    return pathname === href || pathname.startsWith(href + '/');
  }

  const linkStyle = (active: boolean) => ({
    fontSize: '14px', fontWeight: 400,
    color: active ? c.signal : c.textMuted,
    borderBottom: active ? `2px solid ${c.signal}` : '2px solid transparent',
    paddingBottom: '2px', textDecoration: 'none',
    transition: 'color 150ms', cursor: 'pointer',
  } as const);

  // PRI-512: About/company links (Blog, Values, Team, Hiring, Use Cases)
  // removed from the app nav — that content lives on the www marketing site.
  // PRI-310: Docs link hidden during friendly-tester phase.
  const NAV_LINKS: { href: string; label: string }[] = [];

  // PRI-523: Pricing link removed — canonical pricing lives on the www marketing site.

  return (
    <header className="fixed top-0 left-0 right-0 z-[200]" style={{
      height: '64px',
      background: scrolled ? `${c.bgSurface}D9` : `${c.bgSurface}B8`,
      backdropFilter: scrolled ? 'blur(24px) saturate(200%)' : 'blur(20px) saturate(180%)',
      borderBottom: `1px solid ${c.border}`,
      transition: 'background 400ms cubic-bezier(0.4,0,0.2,1)',
    }}>
      <nav role="navigation" aria-label="Main navigation"
        style={{ padding: '0 56px', height: '64px', display: 'flex', alignItems: 'center', position: 'relative' }}>

        {/* Logo */}
        <a href="/" className="flex-shrink-0" style={{ display: 'flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
          <img src="/Darwin_v3_vinyl_transparent.png" alt="" style={{ height: 34, width: 'auto', objectFit: 'contain' }} />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
            <span style={{ fontSize: 16, fontWeight: 300, color: c.textPrimary, letterSpacing: '-0.025em' }}>Primate</span>
            <span style={{ fontSize: 16, fontWeight: 300, color: '#2058F0', letterSpacing: '-0.025em' }}>Intelligence</span>
          </div>
        </a>

        {/* Desktop centre links */}
        <div className="hidden tablet-l:flex" style={{
          position: 'absolute', left: '50%', transform: 'translateX(-50%)',
          alignItems: 'center', gap: '28px',
        }}>
          {NAV_LINKS.map(link => (
            <a key={link.href} href={link.href}
              style={linkStyle(isActive(link.href))}
              onMouseEnter={e => { (e.target as HTMLElement).style.color = c.textPrimary; }}
              onMouseLeave={e => { (e.target as HTMLElement).style.color = isActive(link.href) ? c.signal : c.textMuted; }}>
              {link.label}
            </a>
          ))}

          {/* PRI-512: Company dropdown removed — About content lives on www. */}
          {/* PRI-523: Pricing link removed — pricing is linked only from www. */}
        </div>

        {/* Desktop right CTA */}
        <div className="hidden tablet-l:flex" style={{ marginLeft: 'auto', alignItems: 'center', gap: '16px' }}>
          {isSignedIn ? (
            <>
              <button onClick={signOut ?? undefined}
                style={{ fontSize: '14px', fontWeight: 400, color: c.textPrimary, background: 'none', border: 'none', cursor: 'pointer', opacity: 0.7 }}
                onMouseEnter={e => { (e.target as HTMLElement).style.opacity = '1'; }}
                onMouseLeave={e => { (e.target as HTMLElement).style.opacity = '0.7'; }}>
                Sign out
              </button>
              <a href="/dashboard"
                style={{ background: c.signal, color: c.textPrimary, borderRadius: '2px', padding: '9px 22px', fontSize: '13px', fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = c.signalHover; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = c.signal; }}>
                Dashboard
              </a>
            </>
          ) : (
            <>
              <a href="/login"
                style={{ fontSize: '14px', fontWeight: 400, color: c.textMuted, textDecoration: 'none' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = c.textPrimary; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = c.textMuted; }}>
                Log in
              </a>
              <a href="/signup"
                style={{ background: c.signal, color: c.textPrimary, borderRadius: '2px', padding: '9px 22px', fontSize: '13px', fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = c.signalHover; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = c.signal; }}>
                Sign up free
              </a>
            </>
          )}
        </div>

        {/* Mobile hamburger button */}
        <button type="button" onClick={() => setMobileOpen(true)} aria-label="Open menu"
          className="tablet-l:hidden"
          style={{ marginLeft: 'auto', width: 44, height: 44, alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {[0,1,2].map(i => <div key={i} style={{ width: 22, height: 2, background: c.textPrimary, borderRadius: 1 }} />)}
          </div>
        </button>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div onClick={() => setMobileOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 400, display: 'flex' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: 280, height: '100%', background: c.bgSurface, padding: '20px', display: 'flex', flexDirection: 'column', marginLeft: 'auto', overflowY: 'auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, paddingBottom: 16, borderBottom: `1px solid ${c.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <img src="/Darwin_v3_vinyl_transparent.png" alt="" style={{ height: 24, width: 'auto', objectFit: 'contain' }} />
                <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
                  <span style={{ fontSize: 12, fontWeight: 300, color: c.textPrimary, letterSpacing: '-0.02em' }}>Primate</span>
                  <span style={{ fontSize: 12, fontWeight: 300, color: '#2058F0', letterSpacing: '-0.02em' }}>Intelligence</span>
                </div>
              </div>
              <button onClick={() => setMobileOpen(false)} aria-label="Close menu"
                style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', color: c.textMuted, fontSize: 20 }}>
                ✕
              </button>
            </div>
            {/* Links */}
            <a href="/"
              style={{ display: 'flex', alignItems: 'center', height: 52, fontSize: 16, fontWeight: 500, color: isActive('/') ? c.signal : c.textPrimary, textDecoration: 'none', borderBottom: `1px solid ${c.border}80` }}>
              Primate Vision
            </a>
            {NAV_LINKS.map(link => (
              <a key={link.href} href={link.href}
                style={{ display: 'flex', alignItems: 'center', height: 52, fontSize: 16, fontWeight: 500, color: isActive(link.href) ? c.signal : c.textPrimary, textDecoration: 'none', borderBottom: `1px solid ${c.border}80` }}>
                {link.label}
              </a>
            ))}
            {/* PRI-512: Company expandable removed — About content lives on www. */}
            {/* PRI-523: Pricing link removed — pricing is linked only from www. */}
            {/* CTA */}
            <div style={{ marginTop: 'auto', paddingTop: 24 }}>
              {isSignedIn ? (
                <>
                  <a href="/dashboard"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 48, background: c.signal, color: c.textPrimary, fontSize: 15, fontWeight: 600, borderRadius: 2, textDecoration: 'none', marginBottom: 10 }}>
                    Dashboard →
                  </a>
                  <button onClick={signOut ?? undefined}
                    style={{ width: '100%', height: 44, background: 'transparent', border: `1px solid ${c.border}`, borderRadius: 2, color: c.textPrimary, fontSize: 14, cursor: 'pointer', opacity: 0.7 }}>
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <a href="/signup"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 48, background: c.signal, color: c.textPrimary, fontSize: 15, fontWeight: 600, borderRadius: 2, textDecoration: 'none', marginBottom: 10 }}>
                    Sign up free
                  </a>
                  <a href="/login"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 44, background: 'transparent', border: `1px solid ${c.border}`, borderRadius: 2, color: c.textMuted, fontSize: 14, textDecoration: 'none' }}>
                    Log in
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
